// src/pages/Campaigns/SurveyGenie/SurveyResponses.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Toolbar,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    Checkbox,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
    Snackbar,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    Divider
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    FilterList as FilterIcon,
    MoreVert as MoreIcon,
    CheckCircle as CompleteIcon,
    Schedule as PendingIcon,
    Cancel as AbandonedIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Devices as DeviceIcon,
    Language as LanguageIcon,
    AccessTime as TimeIcon,
    SentimentSatisfiedAlt as PositiveIcon,
    SentimentDissatisfied as NegativeIcon,
    SentimentNeutral as NeutralIcon,
    Psychology as MixedIcon,
    Analytics as AnalyticsIcon,
    ArrowBack as BackIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from '../../../../utils/axiosInstance'; // Changed import

// ==================== SENTIMENT BADGE COMPONENT ====================
const SentimentBadge = ({ sentiment, score, size = 'small', showScore = false }) => {
    const getConfig = () => {
        switch (sentiment?.toUpperCase()) {
            case 'POSITIVE':
                return { 
                    icon: <PositiveIcon fontSize="small" />, 
                    color: 'success', 
                    label: 'Positive',
                    bgColor: '#e8f5e9'
                };
            case 'NEGATIVE':
                return { 
                    icon: <NegativeIcon fontSize="small" />, 
                    color: 'error', 
                    label: 'Negative',
                    bgColor: '#ffebee'
                };
            case 'NEUTRAL':
                return { 
                    icon: <NeutralIcon fontSize="small" />, 
                    color: 'default', 
                    label: 'Neutral',
                    bgColor: '#f5f5f5'
                };
            case 'MIXED':
                return { 
                    icon: <MixedIcon fontSize="small" />, 
                    color: 'warning', 
                    label: 'Mixed',
                    bgColor: '#fff3e0'
                };
            default:
                return null;
        }
    };

    const config = getConfig();
    if (!config) return <Typography variant="caption" color="text.secondary">—</Typography>;

    return (
        <Chip
            icon={config.icon}
            label={showScore && score !== undefined ? `${config.label} (${Math.round(score * 100)}%)` : config.label}
            color={config.color}
            size={size}
            sx={{ fontWeight: 500 }}
        />
    );
};

// ==================== SENTIMENT FILTER COMPONENT ====================
const SentimentFilter = ({ value, onChange }) => {
    const sentimentOptions = [
        { value: 'all', label: 'All Sentiments', icon: null },
        { value: 'POSITIVE', label: 'Positive', icon: <PositiveIcon sx={{ color: '#4caf50' }} />, color: '#4caf50' },
        { value: 'NEGATIVE', label: 'Negative', icon: <NegativeIcon sx={{ color: '#f44336' }} />, color: '#f44336' },
        { value: 'NEUTRAL', label: 'Neutral', icon: <NeutralIcon sx={{ color: '#9e9e9e' }} />, color: '#9e9e9e' },
        { value: 'MIXED', label: 'Mixed', icon: <MixedIcon sx={{ color: '#ff9800' }} />, color: '#ff9800' }
    ];

    return (
        <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sentiment</InputLabel>
            <Select
                value={value}
                label="Sentiment"
                onChange={(e) => onChange(e.target.value)}
            >
                {sentimentOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {option.icon}
                            <span>{option.label}</span>
                        </Box>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

// ==================== STATUS FILTER COMPONENT ====================
const StatusFilter = ({ value, onChange }) => {
    const statusOptions = [
        { value: 'all', label: 'All Status' },
        { value: 'COMPLETED', label: 'Completed', color: 'success' },
        { value: 'IN_PROGRESS', label: 'In Progress', color: 'warning' },
        { value: 'ABANDONED', label: 'Abandoned', color: 'error' }
    ];

    return (
        <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
                value={value}
                label="Status"
                onChange={(e) => onChange(e.target.value)}
            >
                {statusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

// ==================== RESPONSE DETAIL DIALOG ====================
const ResponseDetailDialog = ({ open, onClose, response, survey }) => {
    // const theme = useTheme(); // Unused
    const [activeTab, setActiveTab] = useState(0);

    if (!response) return null;

    const getAnswerDisplay = (answer, question) => {
        if (!answer) return '—';
        
        switch (question?.question_type) {
            case 'MULTIPLE_CHOICE':
            case 'SINGLE_CHOICE':
            case 'DROPDOWN':
                return answer.selected_options?.map(opt => opt.option_text).join(', ') || answer.answer_text || '—';
            case 'RATING':
            case 'NPS':
            case 'SLIDER':
                return answer.answer_number !== null ? answer.answer_number : '—';
            case 'DATE':
                return answer.answer_date ? format(new Date(answer.answer_date), 'PPP') : '—';
            case 'FILE_UPLOAD':
                return answer.file_url ? (
                    <Button size="small" href={answer.file_url} target="_blank">
                        View File
                    </Button>
                ) : '—';
            case 'MATRIX':
                return answer.matrix_answers ? (
                    <Box>
                        {Object.entries(answer.matrix_answers).map(([row, col]) => (
                            <Typography key={row} variant="body2">
                                {row}: {col}
                            </Typography>
                        ))}
                    </Box>
                ) : '—';
            default:
                return answer.answer_text || '—';
        }
    };

    const getSentimentColor = (sentiment) => {
        switch (sentiment) {
            case 'POSITIVE': return '#4caf50'; // theme.palette.success.light replacement
            case 'NEGATIVE': return '#f44336'; // theme.palette.error.light replacement
            case 'MIXED': return '#ff9800'; // theme.palette.warning.light replacement
            default: return 'transparent';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6">Response Details</Typography>
                    <Chip 
                        label={response.status}
                        color={response.status === 'COMPLETED' ? 'success' : response.status === 'IN_PROGRESS' ? 'warning' : 'error'}
                        size="small"
                    />
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
                    <Tab label="Answers" />
                    <Tab label="Respondent Info" />
                    <Tab label="Metadata" />
                </Tabs>

                {activeTab === 0 && (
                    <Box>
                        {response.answers?.map((answer, index) => {
                            const question = survey?.questions?.find(q => q.id === answer.question_id);
                            const isTextQuestion = ['TEXT_SHORT', 'TEXT_LONG'].includes(question?.question_type);
                            
                            return (
                                <Paper 
                                    key={answer.id || index} 
                                    variant="outlined" 
                                    sx={{ 
                                        p: 2, 
                                        mb: 2,
                                        borderLeft: isTextQuestion && answer.sentiment 
                                            ? `4px solid ${getSentimentColor(answer.sentiment)}`
                                            : undefined
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Q{index + 1}: {question?.question_text || 'Question'}
                                    </Typography>
                                    <Typography variant="body1">
                                        {getAnswerDisplay(answer, question)}
                                    </Typography>
                                    
                                    {/* Show sentiment for text answers */}
                                    {isTextQuestion && answer.sentiment && (
                                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SentimentBadge 
                                                sentiment={answer.sentiment} 
                                                score={answer.sentiment_score}
                                                showScore={true}
                                            />
                                        </Box>
                                    )}
                                </Paper>
                            );
                        })}
                        
                        {(!response.answers || response.answers.length === 0) && (
                            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                No answers recorded
                            </Typography>
                        )}
                    </Box>
                )}

                {activeTab === 1 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <PersonIcon color="action" />
                                        <Typography variant="subtitle2">Name</Typography>
                                    </Box>
                                    <Typography>{response.respondent_name || 'Anonymous'}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <EmailIcon color="action" />
                                        <Typography variant="subtitle2">Email</Typography>
                                    </Box>
                                    <Typography>{response.respondent_email || '—'}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <PhoneIcon color="action" />
                                        <Typography variant="subtitle2">Phone</Typography>
                                    </Box>
                                    <Typography>{response.respondent_phone || '—'}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <LanguageIcon color="action" />
                                        <Typography variant="subtitle2">Country</Typography>
                                    </Box>
                                    <Typography>{response.country || '—'}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {activeTab === 2 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <DeviceIcon color="action" />
                                        <Typography variant="subtitle2">Device</Typography>
                                    </Box>
                                    <Typography>{response.device_type || 'Unknown'}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <LanguageIcon color="action" />
                                        <Typography variant="subtitle2">Browser</Typography>
                                    </Box>
                                    <Typography>{response.browser || 'Unknown'}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <TimeIcon color="action" />
                                        <Typography variant="subtitle2">Started At</Typography>
                                    </Box>
                                    <Typography>
                                        {response.started_at ? format(new Date(response.started_at), 'PPpp') : '—'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <TimeIcon color="action" />
                                        <Typography variant="subtitle2">Completed At</Typography>
                                    </Box>
                                    <Typography>
                                        {response.completed_at ? format(new Date(response.completed_at), 'PPpp') : '—'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <TimeIcon color="action" />
                                        <Typography variant="subtitle2">Duration</Typography>
                                    </Box>
                                    <Typography>
                                        {response.duration_seconds 
                                            ? `${Math.floor(response.duration_seconds / 60)}m ${response.duration_seconds % 60}s`
                                            : '—'
                                        }
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <LanguageIcon color="action" />
                                        <Typography variant="subtitle2">IP Address</Typography>
                                    </Box>
                                    <Typography>{response.ip_address || '—'}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

// ==================== MAIN COMPONENT ====================
const SurveyResponses = () => {
    const { chatbotId, surveyId } = useParams();
    const navigate = useNavigate();
    // const theme = useTheme(); // Unused

    // State
    const [loading, setLoading] = useState(true);
    const [responses, setResponses] = useState([]);
    const [survey, setSurvey] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('completed_at');
    const [order, setOrder] = useState('desc');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sentimentFilter, setSentimentFilter] = useState('all');
    const [selectedResponses, setSelectedResponses] = useState([]);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [sentimentStats, setSentimentStats] = useState(null);

    // Fetch survey details
    const fetchSurvey = useCallback(async () => {
        try {
            // Updated to use axiosInstance and path without /api prefix
            // Wait, axiosInstance config usually has baseURL ending in /api or the user appends it.
            // Based on user prompt: "axiosInstance.get('/api/surveys/analysis/status')"
            // It seems axiosInstance points to the domain, so we include /api.
            const response = await axiosInstance.get(`/api/surveys/${surveyId}`);
            if (response.data.success) {
                setSurvey(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching survey:', error);
        }
    }, [surveyId]);

    // Fetch responses
    const fetchResponses = useCallback(async () => {
        setLoading(true);
        try {
            let url = '/api/surveys/responses';
            const params = {
                survey_id: surveyId,
                page: page + 1,
                per_page: rowsPerPage,
                sort_by: orderBy,
                sort_order: order
            };

            // Add filters
            if (searchQuery) {
                params.search = searchQuery;
            }
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            // Use sentiment filter endpoint if filtering by sentiment
            if (sentimentFilter !== 'all') {
                url = '/api/surveys/responses/filter-sentiment';
                params.sentiment = sentimentFilter;
            }

            // Updated to use axiosInstance
            const response = await axiosInstance.get(url, { params });
            
            if (response.data.success) {
                setResponses(response.data.data);
                setTotalCount(response.data.pagination?.total || response.data.total || response.data.data.length);
            }
        } catch (error) {
            console.error('Error fetching responses:', error);
            setSnackbar({
                open: true,
                message: 'Failed to fetch responses',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    }, [surveyId, page, rowsPerPage, orderBy, order, searchQuery, statusFilter, sentimentFilter]);

    // Fetch sentiment statistics
    const fetchSentimentStats = useCallback(async () => {
        try {
            // Updated to use axiosInstance
            const response = await axiosInstance.get('/api/surveys/analysis/sentiment', {
                params: { survey_id: surveyId }
            });
            if (response.data.success) {
                setSentimentStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching sentiment stats:', error);
        }
    }, [surveyId]);

    // Initial fetch
    useEffect(() => {
        fetchSurvey();
        fetchSentimentStats();
    }, [fetchSurvey, fetchSentimentStats]);

    // Fetch responses when filters change
    useEffect(() => {
        fetchResponses();
    }, [fetchResponses]);

    // Get overall sentiment for a response
    const getResponseSentiment = (response) => {
        if (!response.answers) return null;
        
        const textAnswers = response.answers.filter(a => a.sentiment && a.answer_text);
        if (textAnswers.length === 0) return null;
        
        const sentimentCounts = { POSITIVE: 0, NEGATIVE: 0, NEUTRAL: 0, MIXED: 0 };
        textAnswers.forEach(a => {
            if (a.sentiment) sentimentCounts[a.sentiment]++;
        });
        
        const dominant = Object.entries(sentimentCounts).sort((a, b) => b[1] - a[1])[0];
        return dominant[1] > 0 ? dominant[0] : null;
    };

    // Get sentiment row color
    const getSentimentRowColor = (sentiment) => {
        switch (sentiment) {
            case 'POSITIVE': return 'rgba(76, 175, 80, 0.04)';
            case 'NEGATIVE': return 'rgba(244, 67, 54, 0.04)';
            case 'MIXED': return 'rgba(255, 152, 0, 0.04)';
            default: return 'transparent';
        }
    };

    // Handle sort
    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Handle select all
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedResponses(responses.map(r => r.id));
        } else {
            setSelectedResponses([]);
        }
    };

    // Handle select one
    const handleSelectOne = (id) => {
        setSelectedResponses(prev => {
            if (prev.includes(id)) {
                return prev.filter(r => r !== id);
            }
            return [...prev, id];
        });
    };

    // Handle view response
    const handleViewResponse = (response) => {
        setSelectedResponse(response);
        setDetailDialogOpen(true);
    };

    // Handle delete responses
    const handleDeleteResponses = async () => {
        if (selectedResponses.length === 0) return;
        
        try {
            // Updated to use axiosInstance
            await axiosInstance.delete('/api/surveys/responses', {
                data: { response_ids: selectedResponses }
            });
            
            setSnackbar({
                open: true,
                message: `${selectedResponses.length} response(s) deleted`,
                severity: 'success'
            });
            setSelectedResponses([]);
            fetchResponses();
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to delete responses',
                severity: 'error'
            });
        }
        setAnchorEl(null);
    };

    // Handle export
    const handleExport = async (format = 'csv') => {
        try {
            // Updated to use axiosInstance
            const response = await axiosInstance.get('/api/surveys/responses/export', {
                params: { survey_id: surveyId, format },
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `survey-${surveyId}-responses.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to export responses',
                severity: 'error'
            });
        }
        setAnchorEl(null);
    };

    // Status chip color
    const getStatusColor = (status) => {
        switch (status) {
            case 'COMPLETED': return 'success';
            case 'IN_PROGRESS': return 'warning';
            case 'ABANDONED': return 'error';
            default: return 'default';
        }
    };

    // Status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'COMPLETED': return <CompleteIcon fontSize="small" />;
            case 'IN_PROGRESS': return <PendingIcon fontSize="small" />;
            case 'ABANDONED': return <AbandonedIcon fontSize="small" />;
            default: return null;
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)}>
                        <BackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            Survey Responses
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {survey?.title || 'Loading...'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AnalyticsIcon />}
                        component={Link}
                        to={`/chatbot/${chatbotId}/surveys/analytics/${surveyId}`}
                    >
                        Analytics
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<MixedIcon />}
                        component={Link}
                        to={`/chatbot/${chatbotId}/surveys/text-analysis/${surveyId}`}
                    >
                        Text Analysis
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={600} color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                {totalCount}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Responses
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={600} color="success.main" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                {sentimentStats?.sentiment_breakdown?.POSITIVE || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Positive
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={600} color="error.main" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                {sentimentStats?.sentiment_breakdown?.NEGATIVE || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Negative
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={600} color="text.secondary" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                {sentimentStats?.sentiment_breakdown?.NEUTRAL || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Neutral
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters and Table */}
            <Paper>
                {/* Toolbar */}
                <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search responses..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                        sx={{ minWidth: 250 }}
                    />
                    
                    <StatusFilter value={statusFilter} onChange={setStatusFilter} />
                    <SentimentFilter value={sentimentFilter} onChange={setSentimentFilter} />
                    
                    <Box sx={{ flex: 1 }} />
                    
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchResponses}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    
                    <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                        <MoreIcon />
                    </IconButton>
                    
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                    >
                        <MenuItem onClick={() => handleExport('csv')}>
                            <ListItemIcon><DownloadIcon /></ListItemIcon>
                            <ListItemText>Export as CSV</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleExport('xlsx')}>
                            <ListItemIcon><DownloadIcon /></ListItemIcon>
                            <ListItemText>Export as Excel</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem 
                            onClick={handleDeleteResponses}
                            disabled={selectedResponses.length === 0}
                            sx={{ color: 'error.main' }}
                        >
                            <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
                            <ListItemText>Delete Selected ({selectedResponses.length})</ListItemText>
                        </MenuItem>
                    </Menu>
                </Toolbar>

                {/* Selected count banner */}
                {selectedResponses.length > 0 && (
                    <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
                        {selectedResponses.length} response(s) selected
                    </Alert>
                )}

                {/* Table */}
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 700 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedResponses.length > 0 && selectedResponses.length < responses.length}
                                        checked={responses.length > 0 && selectedResponses.length === responses.length}
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'id'}
                                        direction={orderBy === 'id' ? order : 'asc'}
                                        onClick={() => handleSort('id')}
                                    >
                                        ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Respondent</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'completed_at'}
                                        direction={orderBy === 'completed_at' ? order : 'asc'}
                                        onClick={() => handleSort('completed_at')}
                                    >
                                        Completed
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Sentiment</TableCell>
                                <TableCell>Device</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : responses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            No responses found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                responses.map((response) => {
                                    const sentiment = getResponseSentiment(response);
                                    return (
                                        <TableRow
                                            key={response.id}
                                            hover
                                            selected={selectedResponses.includes(response.id)}
                                            sx={{ 
                                                backgroundColor: getSentimentRowColor(sentiment),
                                                '&:hover': {
                                                    backgroundColor: getSentimentRowColor(sentiment)
                                                }
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedResponses.includes(response.id)}
                                                    onChange={() => handleSelectOne(response.id)}
                                                />
                                            </TableCell>
                                            <TableCell>#{response.id}</TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2">
                                                        {response.respondent_name || 'Anonymous'}
                                                    </Typography>
                                                    {response.respondent_email && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {response.respondent_email}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {response.completed_at 
                                                    ? format(new Date(response.completed_at), 'MMM d, yyyy h:mm a')
                                                    : '—'
                                                }
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(response.status)}
                                                    label={response.status?.replace('_', ' ')}
                                                    color={getStatusColor(response.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <SentimentBadge sentiment={sentiment} />
                                            </TableCell>
                                            <TableCell>
                                                {response.device_type || 'Unknown'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="View Details">
                                                    <IconButton 
                                                        size="small"
                                                        onClick={() => handleViewResponse(response)}
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>

            {/* Response Detail Dialog */}
            <ResponseDetailDialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                response={selectedResponse}
                survey={survey}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SurveyResponses;